import { differenceInMinutes } from 'date-fns';
import convertEventResponseToUiData from '@components/SchedulePage/utilities/convertEventResponseToUiData/convertEventResponseToUiData';
import calculateBlockDetails from '@components/SchedulePage/utilities/calculateBlockDetails/calculateBlockDetails';
import minuteDurationToHrMinStr from '@utilities/minuteDurationToHrMinStr/minuteDurationToHrMinStr';
import sortEvents from '@components/SchedulePage/utilities/sortEvents/sortEvents';
import sortTimeFrames from '@components/SchedulePage/utilities/sortTimeFrames/sortTimeFrames';
import { ScheduleApiResponse } from '@data/schedules/types/ScheduleApiResponse';
import { ScheduleColumnData } from '@data/schedules/types/ScheduleColumnData';
import SurgeonWithDisplayName from '@data/surgeon/SurgeonWithDisplayName';
import { decorateEventsWithGaps } from '@components/SchedulePage/utilities/decorateEventsWithGaps/decorateEventsWithGaps';
import { toZonedTime } from 'date-fns-tz';
import { sortObjectsByField } from '@utilities/sortObjectsByField/sortObjectsByField';
import { checkIsBlockReleased } from '@components/SchedulePage/utilities/checkIsBlockReleased/checkIsBlockReleased';
import {
	combineTimeFrames, extractBlockReleaseDetails,
	isNonReleasedSurgeonBlock
} from '@components/SchedulePage/utilities/blockTimeframeUtils/blockTimeframeUtils';

/**
 * Convert surgeontimes API response to ScheduleColumnData for UI
 *
 * @param schedules {ScheduleApiResponse[]} - SurgeonTimes API response
 * @param surgeonsById {Record<string, SurgeonWithDisplayName>} - Surgeon data organized by id
 * @param selectedDate {Date} - Date selected by user on scheduling page
 * @param hospitalTimeZone {string} - Timezone of the hospital
 *
 * @returns {ScheduleColumnData[]} - Schedule data for UI
 */
const convertSchedulesResponseToUiData = (
	schedules: ScheduleApiResponse[],
	surgeonsById: Record<string, SurgeonWithDisplayName>,
	selectedDate: Date,
	hospitalTimeZone: string,
): ScheduleColumnData[] => {
	return schedules
		.map((schedule): ScheduleColumnData => {
			const releaseDetails = extractBlockReleaseDetails(schedule.blocks || []);
			const {
				isReleased,
				releasedTimeFrames,
			} = releaseDetails;

			let { nonReleasedTimeFrames, surgeonTimeFrames } = releaseDetails;

			// If there are multiple nonReleasedTimeframes with same id and consecutive timeframes, combine them
			nonReleasedTimeFrames = combineTimeFrames(
				nonReleasedTimeFrames,
				schedule.blocks?.filter(block => !checkIsBlockReleased(block)) || [],
			);

			surgeonTimeFrames = combineTimeFrames(
				surgeonTimeFrames,
				schedule.blocks?.filter(isNonReleasedSurgeonBlock) || [],
			);

			const timeframes = (isReleased
				? releasedTimeFrames
				: nonReleasedTimeFrames)
				.sort(sortTimeFrames);

			const events = schedule.events
				?.map(convertEventResponseToUiData(schedule.owner))
				.sort(sortEvents) || [];

			const { toFollowTime = nonReleasedTimeFrames[0]?.start } = calculateBlockDetails(timeframes, events);
			const { overlapMinutes } = calculateBlockDetails(surgeonTimeFrames.length > 0 ? surgeonTimeFrames : nonReleasedTimeFrames, events);

			const totalMinutes = (surgeonTimeFrames.length > 0 ? surgeonTimeFrames : nonReleasedTimeFrames).reduce(
				(accum, t) => accum + differenceInMinutes(t.end, t.start),
				0,
			);

			const availableTime = Math.max(totalMinutes - overlapMinutes, 0);

			return {
				...schedule,
				surgeon: surgeonsById[schedule.id],
				scheduleHasBlock: !!(
					schedule.blocks && schedule.blocks?.length
				),
				events: decorateEventsWithGaps(events, hospitalTimeZone),
				displayName: schedule.owner,
				isReleased,
				individualBlocks: schedule.blocks || [],
				timeframes,
				availableTime,
				availableTimeDisplay: minuteDurationToHrMinStr(availableTime),
				toFollowTime: toFollowTime && toZonedTime(toFollowTime, hospitalTimeZone),
				selectedDate,
				utilization: schedule.utilization,
			};
		})
		.sort(sortObjectsByField<ScheduleColumnData>('displayName'));
};

export default convertSchedulesResponseToUiData;
